import type { OrgInfo } from "@/stores/management";
import { api } from "./api";

export interface MemberInfo {
  id: string;
  user_id: string;
  org_id: string;
  role_id: string;
  user_name: string | null;
  user_account: string | null;
  joined_at: string;
}

export interface RoleInfo {
  id: string;
  org_id: string;
  name: string;
  permissions: string[];
  is_default: boolean;
  created_at: string;
}

export interface SsoConfigInfo {
  id: string;
  org_id: string;
  provider_name: string;
  issuer_url: string;
  client_id: string;
  scopes: string[];
  default_role_id: string;
  account_bind_field: string;
  auto_create_user: boolean;
  enabled: boolean;
  created_at: string;
  updated_at: string;
}

export const orgService = {
  myPermissions: (orgId: string) => api.get<string[]>(`/orgs/${orgId}/my-permissions`),
  list: () => api.get<OrgInfo[]>("/orgs"),
  get: (id: string) => api.get<OrgInfo>(`/orgs/${id}`),
  create: (name: string) => api.post<OrgInfo>("/orgs", { name }),
  update: (id: string, data: { name?: string; plan?: string }) =>
    api.put<OrgInfo>(`/orgs/${id}`, data),
  delete: (id: string) => api.delete(`/orgs/${id}`),

  listMembers: (orgId: string) =>
    api.get<MemberInfo[]>(`/orgs/${orgId}/members`),
  inviteMember: (orgId: string, account: string, role_id?: string) =>
    api.post<MemberInfo>(`/orgs/${orgId}/members`, { account, role_id }),
  updateMember: (orgId: string, userId: string, role_id: string) =>
    api.put(`/orgs/${orgId}/members/${userId}`, { role_id }),
  removeMember: (orgId: string, userId: string) =>
    api.delete(`/orgs/${orgId}/members/${userId}`),

  listRoles: (orgId: string) =>
    api.get<RoleInfo[]>(`/orgs/${orgId}/roles`),
  createRole: (
    orgId: string,
    data: { name: string; permissions: string[]; is_default?: boolean },
  ) => api.post<RoleInfo>(`/orgs/${orgId}/roles`, data),
  updateRole: (
    orgId: string,
    roleId: string,
    data: { name?: string; permissions?: string[]; is_default?: boolean },
  ) => api.put<RoleInfo>(`/orgs/${orgId}/roles/${roleId}`, data),
  deleteRole: (orgId: string, roleId: string) =>
    api.delete(`/orgs/${orgId}/roles/${roleId}`),

  getSso: (orgId: string) =>
    api.get<SsoConfigInfo | null>(`/orgs/${orgId}/sso`),
  createSso: (orgId: string, data: Omit<SsoConfigInfo, "id" | "org_id" | "enabled" | "created_at" | "updated_at"> & { client_secret: string }) =>
    api.post<SsoConfigInfo>(`/orgs/${orgId}/sso`, data),
  updateSso: (orgId: string, data: Record<string, unknown>) =>
    api.put<SsoConfigInfo>(`/orgs/${orgId}/sso`, data),
  deleteSso: (orgId: string) => api.delete(`/orgs/${orgId}/sso`),
};
